import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Shield, UserCircle2, Users } from 'lucide-react';
import Modal from '../../components/ui/Modal';
import { api } from '../../lib/api';
import { useAuthStore } from '../../store/authStore';
import { toFrontendUser } from '../../lib/backendAdapters';

const AdminTeam: React.FC = () => {
    const { user } = useAuthStore();
    const [members, setMembers] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [creatingMember, setCreatingMember] = useState(false);

    const loadMembers = useCallback(async () => {
        if (!user?.establishmentId) return;
        try {
            setLoading(true);
            const { data } = await api.get(`/users/establishment/${user.establishmentId}`);
            setMembers(data.map(toFrontendUser));
        } catch (error) {
            console.error('Failed to load team', error);
        } finally {
            setLoading(false);
        }
    }, [user?.establishmentId]);

    useEffect(() => {
        loadMembers();
    }, [loadMembers]);

    const createMember = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!user?.establishmentId) return;

        const formData = new FormData(e.currentTarget);
        const role = formData.get('role') === 'owner' ? 'ADMIN' : 'STAFF';

        try {
            setCreatingMember(true);
            await api.post('/users', {
                name: formData.get('name'),
                email: formData.get('email'),
                password: formData.get('password'),
                role,
                establishmentId: user.establishmentId,
            });
            setModalOpen(false);
            await loadMembers();
        } catch (error) {
            console.error('Failed to create team member', error);
        } finally {
            setCreatingMember(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-white">Equipe</h1>
                    <p className="text-muted-foreground text-sm mt-1">Controle de acesso do time do restaurante</p>
                </div>
                <button onClick={() => setModalOpen(true)} className="btn-primary flex items-center gap-2">
                    <Plus className="w-4 h-4" />
                    Adicionar Membro
                </button>
            </div>

            {loading ? (
                <div className="text-center py-12 text-muted-foreground">Carregando equipe...</div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {members.map((member) => (
                        <div key={member.id} className="card p-5 space-y-4">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-2xl bg-primary/15 flex items-center justify-center">
                                    <UserCircle2 className="w-6 h-6 text-primary" />
                                </div>
                                <div>
                                    <h2 className="text-white font-bold">{member.name}</h2>
                                    <p className="text-sm text-muted-foreground">{member.email}</p>
                                </div>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="badge badge-primary capitalize">{member.role}</span>
                                <div className="flex items-center gap-2 text-muted-foreground text-sm">
                                    <Shield className="w-4 h-4" />
                                    Acesso ativo
                                </div>
                            </div>
                        </div>
                    ))}

                    {members.length === 0 && (
                        <div className="card p-8 text-center text-muted-foreground md:col-span-2 xl:col-span-3">
                            <Users className="w-10 h-10 mx-auto mb-3 opacity-40" />
                            Nenhum membro cadastrado.
                        </div>
                    )}
                </div>
            )}

            <Modal
                isOpen={modalOpen}
                onClose={() => {
                    if (!creatingMember) {
                        setModalOpen(false);
                    }
                }}
                title="Adicionar Membro"
                footer={(
                    <>
                        <button type="button" onClick={() => setModalOpen(false)} disabled={creatingMember} className="btn-secondary disabled:cursor-not-allowed disabled:opacity-60">Cancelar</button>
                        <button type="submit" form="teamForm" disabled={creatingMember} className="btn-primary disabled:cursor-not-allowed disabled:opacity-60">{creatingMember ? 'Criando...' : 'Criar Usuario'}</button>
                    </>
                )}
            >
                <form id="teamForm" onSubmit={createMember} className="space-y-4">
                    <input name="name" required className="input" placeholder="Nome" />
                    <input name="email" required type="email" className="input" placeholder="Email" />
                    <input name="password" required type="password" className="input" placeholder="Senha temporaria" />
                    <select name="role" className="input appearance-none bg-[#1A1A1A]">
                        <option value="attendant">Atendente</option>
                        <option value="owner">Administrador</option>
                    </select>
                </form>
            </Modal>
        </div>
    );
};

export default AdminTeam;
